import { useCallback, useEffect, useMemo, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHead, Stat, Empty, selectCls, btnPrimary, btnGhost } from "@/components/erp/ErpUi";
import { toast } from "sonner";
import { BarChart3, FileSpreadsheet, Loader2 } from "lucide-react";

const p2 = (n) => String(n).padStart(2, "0");
const curMonth = () => { const d = new Date(); return `${d.getFullYear()}-${p2(d.getMonth() + 1)}`; };

const LINES = [
  ["Receita serviços", "revenue_services"],
  ["Receita peças (OS)", "revenue_os_parts"],
  ["Receita balcão", "revenue_counter"],
  ["= Receita total", "revenue_total", "subtotal"],
  ["− Custo de peças", "parts_cost", "neg"],
  ["− Terceirizados", "third_party", "neg"],
  ["= Lucro bruto", "gross_profit", "subtotal"],
  ["− Despesas", "expenses_total", "neg"],
  ["= Lucro líquido", "net_profit", "total"],
  ["Margem (%)", "margin", "pct"],
];

export default function OwnerDRE() {
  const [month, setMonth] = useState(curMonth());
  const [compare, setCompare] = useState(3);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const load = useCallback(() => api.get("/erp/dre", { params: { month, compare } })
    .then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e))), [month, compare]);
  useEffect(() => { load(); }, [load]);
  const months = useMemo(() => (data ? [...data.previous, data.current] : []), [data]);
  const download = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/erp/dre.xlsx", { params: { month, compare }, responseType: "blob" });
      const u = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = u; a.download = `dre-${month}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch (e) { toast.error(formatApiError(e)); } finally { setDownloading(false); }
  };
  const fmt = (v, kind) => kind === "pct" ? `${(v || 0).toFixed(1)}%` : brl(v || 0);
  const cur = data?.current;
  return (
    <div className="space-y-5 animate-fade-up">
      <PageHead icon={BarChart3} kicker="Financeiro" title="DRE e balancete">
        <Button data-testid="dre-export" onClick={download} disabled={downloading || !data} className={btnPrimary}>
          {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />} XLSX
        </Button>
      </PageHead>
      <div className="flex flex-wrap gap-2 items-center">
        <label className="flex items-center gap-2 text-xs text-zinc-400">Mês
          <input data-testid="dre-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="h-10 rounded-md border border-zinc-800 bg-zinc-900 px-2 text-sm text-zinc-100" /></label>
        <label className="flex items-center gap-2 text-xs text-zinc-400">Comparar com
          <select data-testid="dre-compare" value={compare} onChange={(e) => setCompare(Number(e.target.value))} className={`${selectCls} w-auto`}>
            {[0, 1, 3, 6, 12].map((n) => <option key={n} value={n}>{n === 0 ? "só o mês atual" : `${n} ${n === 1 ? "mês anterior" : "meses anteriores"}`}</option>)}
          </select></label>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat testid="dre-revenue" label="Receita total" value={cur ? brl(cur.revenue_total) : "…"} />
        <Stat testid="dre-gross" label="Lucro bruto" value={cur ? brl(cur.gross_profit) : "…"} tone="green" />
        <Stat testid="dre-net" label="Lucro líquido" value={cur ? brl(cur.net_profit) : "…"} tone={cur?.net_profit < 0 ? "red" : "green"} />
        <Stat testid="dre-margin" label="Margem líquida" value={cur ? `${cur.margin.toFixed(1)}%` : "…"} tone={cur?.margin < 10 ? "amber" : "green"} />
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
        <div className="rounded-2xl border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead><tr className="bg-zinc-900 text-[11px] font-mono-caps text-zinc-500 uppercase">
              <th className="text-left px-3 py-2">Linha</th>
              {months.map((m) => <th key={m.month} className="text-right px-3 py-2">{m.month}</th>)}
            </tr></thead>
            <tbody>{LINES.map(([lbl, key, kind]) => (
              <tr key={key} data-testid={`dre-line-${key}`} className={`border-t border-zinc-800 ${kind === "total" || kind === "subtotal" ? "bg-zinc-900/60" : ""}`}>
                <td className={`px-3 py-2 ${kind === "total" ? "font-black text-zinc-100" : kind === "subtotal" ? "font-bold text-zinc-200" : "text-zinc-300"}`}>{lbl}</td>
                {months.map((m) => (
                  <td key={m.month} className={`px-3 py-2 text-right tabular-nums ${kind === "neg" ? "text-red-300" : kind === "total" ? "text-green-neon font-black" : "text-zinc-100"}`}>
                    {fmt(m[key], kind)}
                  </td>
                ))}
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {cur && (
        <div>
          <h2 className="font-display text-lg font-bold uppercase text-zinc-100 mt-6 mb-2">Despesas por centro de custo</h2>
          {cur.expenses_by_center.length === 0 ? <Empty>Sem despesas no mês.</Empty> : (
            <ul className="rounded-xl border border-zinc-800 bg-zinc-950/50">
              {cur.expenses_by_center.map((c) => (
                <li key={c.name} data-testid={`dre-center-${c.name}`} className="flex justify-between items-center px-4 py-2 border-t border-zinc-800 first:border-t-0">
                  <span className="text-zinc-200">{c.name}</span>
                  <span className="text-red-300 font-bold tabular-nums">{brl(c.amount)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-zinc-500 mt-2">Centros de custo cadastrados em Configurações › Financeiro aparecem como opção ao criar uma conta a pagar. Despesas sem centro vão para o grupo da categoria.</p>
        </div>
      )}
    </div>
  );
}
